<template>
  <div
    :id="idMatch"
    class="video-container">
    <div
      :id="`player-${idMatch}`"
      class="player-wrap">
      <video :class="videoClass">
        <source
          v-if="useBRVideoPlayer"
          type="application/dash+xml"
          :ref="`${idMatch}-videoSource`" />
      </video>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../../../../config/config.json';

function resetStreamFlag() {
  const match = this.$store.state.live.matches[this.idMatch];
  if (match) {
    this.$store.dispatch('updateMatchMediaState', {
      idMatch: this.idMatch,
      hasStream: true,
      error: '',
    });
  }
}

const getStreamSources = (urls) => {
  const sources = Object.keys(urls).map((key) => {
    const source = urls[key];
    let type;

    if (source.indexOf('rtmp') > -1) {
      type = 'rtmp/mp4';
    } else if (source.indexOf('.m3u8') > -1) {
      type = 'application/x-mpegURL';
    } else if (source.indexOf('.mpd') > -1) {
      type = 'application/dash+xml';
    }

    return {
      src: source,
      type,
    };
  });

  if (config.videoPlayer !== 'AVVP') {
    sources.reverse();
  }

  return sources;
};

export default {
  name: 'StreamPlayer',
  props: {
    idMatch: {
      required: true,
      type: Number,
    },
    options: {
      required: false,
      type: Object,
      default: () => {},
    },
    index: {
      required: true,
      type: Number,
    },
  },
  data() {
    return {
      healthCheckInterval: 6000,
      prevReadyState: -1,
      errorMessage: '',
      player: null,
      useBRVideoPlayer: config.videoPlayer === 'AVVP',
      streamSources: [],
      globalOptions: {
        muted: true,
        controls: false,
        autoplay: false,
        preload: 'auto',
        controlBar: {
          remainingTimeDisplay: false,
          playToggle: {},
          progressControl: {},
          fullscreenToggle: {},
        },
      },
      videoContainer: null,
      bRVideoPlayerInterval: '',
    };
  },
  updated() {
    this.$nextTick(() => {
      this.streamSources.length = 0;
      this.getStreamUrl();
    });
  },
  mounted() {
    this.$nextTick(() => {
      this.streamSources.length = 0;
      this.getStreamUrl();
    });
  },
  beforeDestroy() {
    if (this.player) {
      if (this.useBRVideoPlayer) {
        clearInterval(this.bRVideoPlayerInterval);
        this.bRVideoPlayerInterval = '';
        this.player.remove();
      } else {
        this.player.dispose();
      }
      this.player = null;
    }
  },
  computed: {
    videoClass() {
      return `video video-js video-${this.idMatch}`;
    },
  },
  methods: {
    getStreamUrl() {
      const { settings } = this.$store.state;
      const { config: appConfig } = this.$store.state;
      const params = {
        cpv: settings.company.cpv,
        language: settings.language,
        matchId: this.idMatch,
        platform: 'retail',
        bitrate: 1264000, // If set format is RTMP (1264000), else SMIL
      };

      return axios
        .get(appConfig.API.stream[settings.env], { params })
        .then((response) => {
          if (response.data.success) {
            this.streamSources = getStreamSources(response.data.url);
            this.errorMessage = '';
          } else {
            this.streamSources = [];
            this.errorMessage = response.data.message;
            this.$log.debug(`Stream error ${this.idMatch} => ${this.errorMessage}`);
          }
        })
        .catch((err) => {
          this.$log.debug(`Error getting stream => ${err}`);
        })
        .then(() => {
          if (this.streamSources.length) {
            this.updateSource();
          } else {
            this.$store.dispatch('updateMatchMediaState', {
              idMatch: this.idMatch,
              hasStream: false,
              error: this.errorMessage,
            });

            // Reload stream every 120s
            setTimeout(resetStreamFlag.bind({
              $store: this.$store,
              idMatch: this.idMatch,
            }), 120 * 1000);
          }
        });
    },

    initBRVideoPlayer() {
      const playerConfig = {
        id: `player-${this.idMatch}`,
        enableUI: false,
        allowFullScreen: false,
        mute: true,
        loglevel: 3,
      };

      this.$refs[`${this.idMatch}-videoSource`].src = this.streamSources[0].src;
      this.player = new avvpl.setupPlayer(playerConfig);

      this.player.canPlayThrough.subscribe(() => {
        this.prevReadyState = -1;
        this.logSourceChanged();
      });

      this.bRVideoPlayerInterval = setInterval(() => {
        if (this.player.getPlayState() === 'in_error') {
          this.$log.debug(`[healthcheck] Ready state issue! - ${this.idMatch}`);
          this.prevReadyState = -1;
          this.getStreamUrl();
          this.player.playVideo();
        }
      }, this.healthCheckInterval);
    },

    initVideoJSPlayer() {
      const elem = this.videoContainer.children[0];
      const videoOptions = Object.assign({}, this.globalOptions, this.options);
      this.player = videojs(elem, videoOptions);

      this.player.on('loadstart', () => {
        if (this.player.paused()) {
          this.player.play().catch((err) => {
            console.log(`[loadstart] "play" error - ${this.idMatch} => `, err);
          });
        }
      });

      // Reset prevReadyState when video starts to play
      this.player.on('canplay', () => {
        this.prevReadyState = -1;
      });

      this.player.on('ended', () => {
        this.player.pause();
        this.$log.debug(`[ended] Player ended! - ${this.idMatch}`);
      });

      // Health check interval (6s)
      this.player.setInterval(() => {
        const readyState = this.player.readyState();
        // 1 - HAVE_METADATA, 2 - HAVE_CURRENT_DATA
        if (readyState === 1 || readyState === 2) {
          if (readyState === this.prevReadyState) {
            this.$log.debug(`[healthcheck] Ready state issue! - ${this.idMatch}`);
            this.prevReadyState = -1;
            this.getStreamUrl();
            return;
          }
        }

        const playerError = this.player.error();
        if (playerError) {
          this.$log.debug(`[healthcheck] Player error - ${this.idMatch}`, playerError);
          this.prevReadyState = -1;
          this.getStreamUrl();
          return;
        }

        if (this.$store.state.connected) {
          if (this.prevReadyState === 0 && readyState === 0) {
            this.$log.debug(`[healthcheck] Ready state issue! - ${this.idMatch}`);
            this.prevReadyState = -1;
            this.getStreamUrl();
            return;
          }
        }

        this.prevReadyState = readyState;
      }, this.healthCheckInterval);

      this.player.ready(() => {
        this.player.src(this.streamSources);
        this.logSourceChanged();
      });
    },

    initialize() {
      this.videoContainer = document.getElementById(`${this.idMatch}`);
      if (!this.videoContainer) return;

      if (this.useBRVideoPlayer) {
        this.initBRVideoPlayer();
      } else {
        this.initVideoJSPlayer();
      }
    },
    updateSource() {
      if (this.player) {
        if (this.useBRVideoPlayer) {
          this.player.load(this.streamSources[0].src);
        } else {
          this.player.src(this.streamSources);
        }
      } else {
        this.initialize();
      }
    },
    logSourceChanged() {
      if (this.useBRVideoPlayer) {
        this.$log.debug(`Player source changed - ${this.idMatch}`, this.$refs[`${this.idMatch}-videoSource`].src);
      } else {
        this.$log.debug(`Player source changed - ${this.idMatch}`, this.player.currentSource());
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/style/variables";

.video-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;

  /deep/ .video-js {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .player-wrap {
    width: 100%;
    display: flex;
    align-items: center;

    .sravvpl_wrapper {
      width: 100%;
      height: 100%;
      display: inline-block;
    }
  }
}
</style>
